/**
 * ResultsToolbar Component
 * Displays results metadata, export button, and view options
 * Shows result count, provides export functionality, and allows view toggling
 *
 * @author SAUBHAGYA Development Team
 * @since US-ORD-006 Phase 5
 */

import React from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { LayoutGrid, List, RefreshCw } from 'lucide-react';
import { ExportButton } from './ExportButton';
import type { OrderSearchResultItem, OrderFilters } from '../types/filter.types';

// ============================================
// TYPES
// ============================================

export interface ResultsToolbarProps {
  totalElements: number;
  currentPage: number;
  pageSize: number;
  orders: OrderSearchResultItem[];
  filters: OrderFilters;
  isLoading?: boolean;
  viewMode?: 'table' | 'grid';
  onViewModeChange?: (mode: 'table' | 'grid') => void;
  onRefresh?: () => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSortChange?: (field: string, direction: 'asc' | 'desc') => void;
}

// ============================================
// SORT OPTIONS
// ============================================

const SORT_OPTIONS = [
  { value: 'orderDate-desc', label: 'Order Date (Newest First)' },
  { value: 'orderDate-asc', label: 'Order Date (Oldest First)' },
  { value: 'totalAmount-desc', label: 'Amount (High to Low)' },
  { value: 'totalAmount-asc', label: 'Amount (Low to High)' },
  { value: 'orderNumber-asc', label: 'Order Number (A-Z)' },
  { value: 'orderNumber-desc', label: 'Order Number (Z-A)' },
  { value: 'customerName-asc', label: 'Customer Name (A-Z)' },
  { value: 'customerName-desc', label: 'Customer Name (Z-A)' },
];

// ============================================
// COMPONENT
// ============================================

export function ResultsToolbar({
  totalElements,
  currentPage,
  pageSize,
  orders,
  filters,
  isLoading = false,
  viewMode = 'table',
  onViewModeChange,
  onRefresh,
  sortBy = 'orderDate',
  sortDirection = 'desc',
  onSortChange,
}: ResultsToolbarProps): JSX.Element {
  // Calculate current range
  const startIndex = (currentPage - 1) * pageSize + 1;
  const endIndex = Math.min(currentPage * pageSize, totalElements);

  // Current sort value
  const currentSort = `${sortBy}-${sortDirection}`;

  /**
   * Handle sort change
   */
  const handleSortChange = (value: string) => {
    if (onSortChange) {
      const [field, direction] = value.split('-') as [string, 'asc' | 'desc'];
      onSortChange(field, direction);
    }
  };

  return (
    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 py-3 px-4 bg-white border-b">
      {/* Left section: Results count */}
      <div className="flex items-center gap-4">
        <div className="text-sm text-gray-700">
          {isLoading ? (
            <span className="animate-pulse">Loading...</span>
          ) : totalElements > 0 ? (
            <span>
              Showing{' '}
              <span className="font-semibold">
                {startIndex}-{endIndex}
              </span>{' '}
              of <span className="font-semibold">{totalElements}</span> orders
            </span>
          ) : (
            <span>No results found</span>
          )}
        </div>

        {onRefresh && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefresh}
            disabled={isLoading}
            aria-label="Refresh results"
          >
            <RefreshCw
              className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`}
            />
          </Button>
        )}
      </div>

      {/* Right section: Controls */}
      <div className="flex items-center gap-2 flex-wrap">
        {/* Sort dropdown */}
        {onSortChange && (
          <Select value={currentSort} onValueChange={handleSortChange}>
            <SelectTrigger className="w-[200px]" aria-label="Sort orders">
              <SelectValue placeholder="Sort by..." />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* View mode toggle */}
        {onViewModeChange && (
          <div className="flex items-center border rounded-md overflow-hidden">
            <Button
              variant={viewMode === 'table' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onViewModeChange('table')}
              className="rounded-none"
              aria-label="Table view"
            >
              <List className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => onViewModeChange('grid')}
              className="rounded-none"
              aria-label="Grid view"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
          </div>
        )}

        {/* Export button */}
        <ExportButton
          orders={orders}
          filters={filters}
        />
      </div>
    </div>
  );
}
